import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Outlet } from 'react-router-dom';
import { signInWithCustomToken, onAuthStateChanged } from 'firebase/auth';
import auth from '../../lib/firebase/auth';
import { apiClient } from '../../services/apiClient';
import { API_ENDPOINTS } from '../../config/api';
import { AuthContext } from '../../context/AuthContext';

// ─── Keyframes (injected once) ────────────────────────────────────────────────
const STYLE_ID = 'firebase-guard-styles';
if (!document.getElementById(STYLE_ID)) {
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = `
    @keyframes fg-fade-in {
      from { opacity: 0; transform: translateY(18px); }
      to   { opacity: 1; transform: translateY(0); }
    }
    @keyframes fg-pulse-ring {
      0%,100% { transform: scale(1);   opacity: 0.4; }
      50%      { transform: scale(1.15); opacity: 0.15; }
    }
    @keyframes fg-spin { to { transform: rotate(360deg); } }
    .fg-card { animation: fg-fade-in 0.4s ease-out both; }
    .fg-ring { animation: fg-pulse-ring 2.4s ease-in-out infinite; }
    .fg-spin { animation: fg-spin 0.7s linear infinite; }
    .fg-btn:hover:not(:disabled) {
      transform: translateY(-1px);
      box-shadow: 0 8px 20px rgba(37,99,235,0.35) !important;
    }
    .fg-btn:active:not(:disabled) { transform: translateY(0); }
    .fg-btn { transition: transform 0.15s ease, box-shadow 0.15s ease; }
  `;
  document.head.appendChild(style);
}

// ─── Loading Screen ───────────────────────────────────────────────────────────
const LoadingScreen = () => (
  <div style={{
    height: '100%', minHeight: '100dvh',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    background: 'linear-gradient(145deg, #f8fafc 0%, #f1f5f9 100%)',
  }}>
    <div className="fg-card" style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1rem',
    }}>
      <span className="fg-spin" style={{
        display: 'inline-block', width: 36, height: 36,
        border: '3px solid #e2e8f0', borderTopColor: '#2563eb', borderRadius: '50%',
      }} />
      <p style={{ margin: 0, fontSize: '0.875rem', color: '#64748b', fontWeight: 500 }}>
        Connecting to chat…
      </p>
    </div>
  </div>
);

// ─── Error Card ───────────────────────────────────────────────────────────────
const ErrorCard = ({ error, isRetrying, onRetry }) => (
  <div style={{
    height: '100%', minHeight: '100dvh',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    background: 'linear-gradient(145deg, #f8fafc 0%, #f1f5f9 100%)',
  }}>
    <div className="fg-card" style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1.5rem',
      padding: '2.75rem 2rem', background: '#ffffff', borderRadius: '1.5rem',
      boxShadow: '0 4px 6px -1px rgba(0,0,0,0.06), 0 12px 36px -6px rgba(0,0,0,0.1)',
      border: '1px solid #e2e8f0', maxWidth: 360, width: '90%', textAlign: 'center',
    }}>
      {/* Icon + pulse ring */}
      <div style={{ position: 'relative', width: 72, height: 72 }}>
        <div className="fg-ring" style={{
          position: 'absolute', inset: -10, borderRadius: '50%',
          border: '2px solid rgba(220,38,38,0.35)',
        }} />
        <div style={{
          width: 72, height: 72, borderRadius: '50%',
          background: 'linear-gradient(145deg, #fee2e2, #fecaca)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: '0 4px 14px rgba(239,68,68,0.2)',
        }}>
          <svg width="30" height="30" viewBox="0 0 24 24" fill="none"
            stroke="#dc2626" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
            <line x1="16" y1="6" x2="22" y2="12"/>
            <line x1="22" y1="6" x2="16" y2="12"/>
          </svg>
        </div>
      </div>

      {/* Text */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
        <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 700, color: '#0f172a' }}>
          Couldn't Connect to Chat
        </h3>
        <p style={{ margin: 0, fontSize: '0.84rem', color: '#64748b', lineHeight: 1.65 }}>
          We had trouble establishing a secure chat session.<br />
          This is usually a temporary issue.
        </p>
        {error && (
          <div style={{
            marginTop: '0.25rem', padding: '0.35rem 0.75rem',
            background: '#f8fafc', border: '1px solid #e2e8f0',
            borderRadius: '0.5rem', fontSize: '0.72rem', color: '#94a3b8',
            fontFamily: 'monospace', lineHeight: 1.5, wordBreak: 'break-word',
          }}>
            {error}
          </div>
        )}
      </div>

      {/* Retry button */}
      <button
        className="fg-btn"
        onClick={onRetry}
        disabled={isRetrying}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem',
          width: '100%', padding: '0.75rem 1.5rem',
          background: isRetrying ? '#94a3b8' : 'linear-gradient(135deg, #1e40af 0%, #2563eb 100%)',
          color: '#fff', border: 'none', borderRadius: '0.75rem',
          fontSize: '0.875rem', fontWeight: 600,
          cursor: isRetrying ? 'not-allowed' : 'pointer',
          boxShadow: isRetrying ? 'none' : '0 4px 14px rgba(37,99,235,0.28)',
        }}
      >
        {isRetrying ? (
          <>
            <span className="fg-spin" style={{
              display: 'inline-block', width: 16, height: 16,
              border: '2px solid rgba(255,255,255,0.3)',
              borderTopColor: '#fff', borderRadius: '50%',
            }} />
            Reconnecting…
          </>
        ) : 'Try Again'}
      </button>
    </div>
  </div>
);

// ─── FirebaseChatGuard ────────────────────────────────────────────────────────
/**
 * Ensures a Firebase session exists before rendering chat routes.
 *
 * Flow:
 *  1. Watch Firebase auth state.
 *  2. Session already active → render <Outlet /> immediately.
 *  3. No session → call POST /v1/chats/generate-token silently,
 *     sign in with the returned token, await onAuthStateChanged confirmation.
 *  4. On error → show error card with "Try Again" (re-runs the auth flow).
 *
 * No redirect to /firebase-tester — auth is fully silent and automatic.
 */
export const FirebaseChatGuard = () => {
  const [isFirebaseReady, setIsFirebaseReady] = useState(false);
  const [authError, setAuthError]             = useState(null);
  const [isRetrying, setIsRetrying]           = useState(false);
  const [retryKey, setRetryKey]               = useState(0);
  const isAuthenticatingRef                   = useRef(false);

  const handleRetry = useCallback(() => {
    isAuthenticatingRef.current = false;
    setAuthError(null);
    setIsRetrying(true);
    setRetryKey(k => k + 1);
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        console.log('[FirebaseChatGuard] ✅ Session active — UID:', firebaseUser.uid);
        setAuthError(null);
        setIsRetrying(false);
        setIsFirebaseReady(true);
        return;
      }

      // Guard against concurrent auth attempts
      if (isAuthenticatingRef.current) return;
      isAuthenticatingRef.current = true;

      console.log('[FirebaseChatGuard] 🔑 No session — requesting token from API…');
      try {
        const response = await apiClient.post(API_ENDPOINTS.chats.generateToken);
        const data  = response.data;
        const token = data?.token ?? data?.firebase_token ?? data?.custom_token;

        if (!token) throw new Error('No token returned by generate-token endpoint.');

        console.log('[FirebaseChatGuard] 🔐 Signing into Firebase…');
        const credential = await signInWithCustomToken(auth, token);
        console.log('[FirebaseChatGuard] ✅ Signed in — UID:', credential.user.uid);
        // onAuthStateChanged fires again → sets isFirebaseReady = true above
      } catch (err) {
        console.error('[FirebaseChatGuard] ❌ Auth failed:', err?.message);
        setAuthError(err?.response?.data?.message || err.message || 'Unknown error.');
        setIsRetrying(false);
      } finally {
        isAuthenticatingRef.current = false;
      }
    });

    return () => unsubscribe();
  // retryKey intentionally re-triggers the effect on manual retry
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [retryKey]);

  if (!isFirebaseReady && authError) {
    return <ErrorCard error={authError} isRetrying={isRetrying} onRetry={handleRetry} />;
  }

  if (!isFirebaseReady) {
    return <LoadingScreen />;
  }

  return <Outlet />;
};

// ─── FirebaseBackgroundConnector ──────────────────────────────────────────────
/**
 * A silent component mounted globally to ensure Firebase is connected
 * even before the user visits the /chat route. This ensures background
 * notifications and unread badges work instantly upon login.
 */
export const FirebaseBackgroundConnector = () => {
  const { user, firebaseUser } = React.useContext(AuthContext);
  const isAuthenticatingRef = useRef(false);

  useEffect(() => {
    let mounted = true;
    let timer = null;
    
    const connect = async () => {
      if (!user || firebaseUser || isAuthenticatingRef.current) return;
      
      try {
        isAuthenticatingRef.current = true;
        console.log('[FirebaseBackgroundConnector] 🔑 Requesting token...');
        
        const res = await apiClient.post(API_ENDPOINTS.chats.generateToken);
        const token = res.data?.token ?? res.data?.firebase_token ?? res.data?.custom_token;
        
        if (!token) {
          console.warn('[FirebaseBackgroundConnector] ⚠️ No token found in response:', res.data);
          return;
        }
        
        if (!mounted) return;
        
        console.log('[FirebaseBackgroundConnector] 🔐 Token received, signing in...');
        await signInWithCustomToken(auth, token);
        console.log('[FirebaseBackgroundConnector] ✅ Sign in successful');
      } catch (err) {
        console.error('[FirebaseBackgroundConnector] ❌ Failed:', err);
      } finally {
        setTimeout(() => { if (mounted) isAuthenticatingRef.current = false; }, 10000);
      }
    };
    
    // Give the dashboard API calls a tiny head-start so this doesn't get dropped
    timer = setTimeout(() => {
      connect();
    }, 1500);
    
    return () => { 
      mounted = false; 
      if (timer) clearTimeout(timer);
    };
  }, [user, firebaseUser]);

  return null;
};

export default FirebaseChatGuard;
